import React, { useState, useEffect, useRef } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  orderService,
  workshopService,
  userService,
  costService,
} from "../services/dataService";
import {
  Order,
  Workshop,
  User,
  OrderStatus,
  OrderWorkshopCost,
} from "../types";
import { useAuth } from "../context/AuthContext";
import { useExchangeRates } from "../context/ExchangeRateContext";
import { useNavigate } from "react-router-dom";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import "./WorkshopKanban.css";
import KanbanCard from "../components/KanbanCard";
import KanbanColumn from "../components/KanbanColumn";
import WorkshopTransferModal from "../components/WorkshopTransferModal";
import OrderEditModal from "../components/OrderEditModal";
import OrderDetailModal from "../components/OrderDetailModal";

const COLUMN_ACCENTS = [
  "#2563eb", "#0891b2", "#7c3aed", "#c026d3",
  "#0d9488", "#db2777", "#4f46e5", "#0369a1", "#ca8a04",
];
const isCompletedWorkshop = (name: string) => {
  const n = (name || "").toLowerCase();
  return n.includes("biten") || n.includes("tamamlan") || n.includes("done");
};
const getAccent = (name: string, idx: number) =>
  isCompletedWorkshop(name) ? "#16a34a" : COLUMN_ACCENTS[idx % COLUMN_ACCENTS.length];

const WorkshopKanban: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { usdRate, eurRate, gbpRate } = useExchangeRates();
  const navigate = useNavigate();
  const kanbanBoardRef = useRef<HTMLDivElement>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  // Kart buton handlers
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);

  const handleEditOrder = (orderId: string) => {
    setEditingOrderId(orderId);
    setEditModalOpen(true);
  };

  const handleDetailOrder = (orderId: string) => {
    setDetailOrderId(orderId);
    setDetailModalOpen(true);
  };

  const handleOrderUpdated = (updatedOrder: Order) => {
    // Orders listesini güncelle
    setOrders((prevOrders) =>
      prevOrders.map((o) =>
        o.orderId === updatedOrder.orderId ? updatedOrder : o,
      ),
    );
  };
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [orderDurations, setOrderDurations] = useState<{
    [orderId: string]: string;
  }>({});

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Transfer modalı için state'ler (operatör + maliyet)
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [pendingWorkshopChange, setPendingWorkshopChange] = useState<{
    orderId: string;
    newWorkshopId: string | null;
    oldWorkshopId: string | undefined;
    orderQuantity?: number;
  } | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>("");

  // Mouse wheel ile yatay scroll için handler
  useEffect(() => {
    const kanbanBoard = kanbanBoardRef.current;
    if (!kanbanBoard) return;

    const handleWheel = (e: WheelEvent) => {
      // Yatay scroll varsa tarayıcının kendi işlemesine izin ver
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        return;
      }

      // Shift basılıysa varsayılan davranışı kullan
      if (e.shiftKey) {
        return;
      }

      // Dikey scroll'u yatay scroll'a çevir
      e.preventDefault();
      kanbanBoard.scrollLeft += e.deltaY;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Sol/Sağ ok tuşları ile scroll
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        kanbanBoard.scrollLeft -= 100;
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        kanbanBoard.scrollLeft += 100;
      }
    };

    kanbanBoard.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      kanbanBoard.removeEventListener("wheel", handleWheel);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Drag sensor ayarları - daha hassas
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
        delay: 100,
        tolerance: 5,
      },
    }),
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Atölyede geçen süreyi hesapla (workshops data ile)
  const calculateWorkshopDurationWithData = (
    logs: any[],
    workshopId: string,
    workshopsData: any[],
  ): string => {
    // Hedef atölye bilgisini bul
    const targetWorkshop = workshopsData.find(
      (w) => w.workshopId === workshopId,
    );
    const targetWorkshopName = targetWorkshop?.name;

    // Eğer atölye adı bulunamadıysa
    if (!targetWorkshopName) {
      return "< 1 dk";
    }

    // Logs'u tarihe göre sırala (changedAt veya timestamp kullan)
    const sortedLogs = [...logs].sort(
      (a, b) =>
        new Date(a.changedAt || a.timestamp).getTime() -
        new Date(b.changedAt || b.timestamp).getTime(),
    );

    let totalDuration = 0;
    let enteredAt: Date | null = null;

    for (let i = 0; i < sortedLogs.length; i++) {
      const log = sortedLogs[i];

      // Log'da atölye değişikliği olup olmadığını kontrol et
      const isWorkshopChange =
        log.changeType === "WorkshopId" ||
        log.changeType === "workshop" ||
        log.changeType === "WorkshopChange" ||
        log.changeType === "WorkshopChanged" ||
        log.fieldName === "WorkshopId" ||
        log.fieldName === "workshop";

      if (!isWorkshopChange) continue;

      // newValue atölye adını veya ID'sini içerebilir
      const logWorkshopName = log.newValue;
      const logWorkshopId = log.newValue;

      // Atölyeye giriş (bu atölyeye taşındı) - hem isim hem ID kontrolü
      if (
        (logWorkshopName === targetWorkshopName ||
          logWorkshopId === workshopId) &&
        !enteredAt
      ) {
        enteredAt = new Date(log.changedAt || log.timestamp);
      }
      // Atölyeden çıkış (başka bir atölyeye taşındı veya atölyeden çıkarıldı)
      else if (
        logWorkshopName !== targetWorkshopName &&
        logWorkshopId !== workshopId &&
        enteredAt
      ) {
        const exitedAt = new Date(log.changedAt || log.timestamp);
        const duration = exitedAt.getTime() - enteredAt.getTime();
        totalDuration += duration;
        enteredAt = null;
      }
    }

    // Hala bu atölyedeyse (son log'da bu atölyeye taşınmış ve hala burada)
    if (enteredAt) {
      const currentDuration = new Date().getTime() - enteredAt.getTime();
      totalDuration += currentDuration;
    }

    // Eğer hiç süre hesaplanmadıysa ve sipariş bu atölyedeyse
    // (yani log kaydı eksikse), siparişin kabul tarihinden itibaren hesapla
    if (totalDuration === 0) {
      return "Hesaplanamadı";
    }

    // Milisaniyeden gün, saat ve dakikaya çevir
    const minutes = Math.floor(totalDuration / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    const remainingMinutes = minutes % 60;

    if (days > 0) {
      return `${days} gün ${remainingHours} saat`;
    } else if (hours > 0) {
      return `${hours} saat ${remainingMinutes} dk`;
    } else if (minutes > 0) {
      return `${minutes} dakika`;
    } else {
      return "< 1 dk";
    }
  };

  // Atölyede geçen süreyi hesapla
  const calculateWorkshopDuration = (
    logs: any[],
    workshopId: string,
  ): string => {
    return calculateWorkshopDurationWithData(logs, workshopId, workshops);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [ordersData, workshopsData, usersData] = await Promise.all([
        orderService.getActiveOrdersForKanban(), // Yeni Kanban endpoint'i - WorkshopDuration dahil
        workshopService.getAll(),
        userService.getAll(),
      ]);

      // Atölyeleri displayOrder'a göre sırala
      const sortedWorkshops = [...workshopsData].sort((a, b) => {
        const orderA = a.displayOrder ?? 999;
        const orderB = b.displayOrder ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name, "tr");
      });

      setOrders(ordersData);
      setWorkshops(sortedWorkshops);
      setUsers(usersData);

      // WorkshopDuration artık backend'den geliyor
      const durations: { [orderId: string]: string } = {};
      for (const order of ordersData) {
        if (order.workshopDuration) {
          durations[order.orderId] = order.workshopDuration;
        }
      }
      setOrderDurations(durations);
    } catch (error) {
      console.error("❌ Failed to load data:", error);
      setAlertModal({
        isOpen: true,
        message: "Veriler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Atölyeye göre siparişleri grupla ve kabul tarihine göre sırala (son gelen en üstte, ilk gelen en altta)
  const matchesFilters = (o: Order): boolean => {
    if (priorityFilter && o.priority !== priorityFilter) return false;
    if (searchTerm.trim()) {
      const q = searchTerm.toLocaleLowerCase("tr");
      const hay = `${o.firm?.firmName || ""} ${o.model?.modelCode || ""} ${o.model?.modelName || ""}`.toLocaleLowerCase(
        "tr",
      );
      if (!hay.includes(q)) return false;
    }
    return true;
  };

  const getOrdersByWorkshop = (workshopId: string): Order[] => {
    return orders
      .filter((order) => order.workshopId === workshopId && matchesFilters(order))
      .sort((a, b) => {
        const dateA = new Date(a.acceptanceDate).getTime();
        const dateB = new Date(b.acceptanceDate).getTime();
        return dateA - dateB; // En eski tarih en üstte, en yeni en altta
      });
  };

  // Kolon için toplam fiyatı TL cinsinden hesapla (tüm dövizleri TL'ye çevir)
  const calculateColumnTotal = (columnOrders: Order[]): number => {
    return columnOrders.reduce((total, order) => {
      const basePrice = (order.price || 0) * order.quantity;
      const currency = order.priceCurrency || order.currency || "TRY";

      // Dövize göre TL'ye çevir
      let priceInTRY = basePrice;
      if (currency === "USD" && usdRate) {
        priceInTRY = basePrice * usdRate;
      } else if (currency === "EUR" && eurRate) {
        priceInTRY = basePrice * eurRate;
      } else if (currency !== "TRY" && currency !== "TL") {
        priceInTRY = 0; // Kur yoksa dönüşüm yapılamaz
      }
      // TRY ise zaten TL

      return total + priceInTRY;
    }, 0);
  };

  // Drag başladığında
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const order = orders.find((o) => o.orderId === active.id);
    if (order) {
      setActiveOrder(order);
    }
  };

  // Drag bittiğinde - atölye değiştir
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    // Drag overlay'i temizle
    setTimeout(() => setActiveOrder(null), 100);

    if (!over) {
      return;
    }

    const draggedOrderId = active.id as string;
    const targetId = over.id as string;
    const draggedOrder = orders.find((o) => o.orderId === draggedOrderId);

    if (!draggedOrder) {
      console.error("❌ Dragged order not found:", draggedOrderId);
      return;
    }

    // Hedef bir kolon (atölye) mu yoksa başka bir kart mı?
    let newWorkshopId: string | null = null;

    // Eğer hedef bir atölye ID'si ise
    if (workshops.some((w) => w.workshopId === targetId)) {
      newWorkshopId = targetId;
    }
    // Eğer hedef başka bir kart ise, o kartın atölyesini al
    else {
      const targetOrder = orders.find((o) => o.orderId === targetId);
      if (targetOrder && targetOrder.workshopId) {
        newWorkshopId = targetOrder.workshopId;
      }
    }

    if (!newWorkshopId) {
      console.error("❌ Could not determine target workshop");
      return;
    }

    if (draggedOrder.workshopId === newWorkshopId) {
      return;
    }

    // Transfer modalını aç (kullanıcı + maliyet)
    setPendingWorkshopChange({
      orderId: draggedOrderId,
      newWorkshopId,
      oldWorkshopId: draggedOrder.workshopId,
      orderQuantity: draggedOrder.quantity,
    });
    setSelectedUserId(draggedOrder.operatorId || "");
    setShowTransferModal(true);
  };

  // Transfer modaldan gelen maliyet kaydet
  const handleTransferSave = async (
    costs: Omit<
      OrderWorkshopCost,
      | "orderWorkshopCostId"
      | "createdAt"
      | "createdBy"
      | "updatedAt"
      | "updatedBy"
      | "order"
      | "workshop"
      | "costItem"
    >[],
  ) => {
    if (!pendingWorkshopChange) return;

    const { orderId, newWorkshopId, oldWorkshopId } = pendingWorkshopChange;
    const draggedOrder = orders.find((o) => o.orderId === orderId);

    if (!draggedOrder) return;

    // Hedef atölyeyi bul
    const targetWorkshop = workshops.find(
      (w) => w.workshopId === newWorkshopId,
    );

    // Önceki atölyeyi bul (maliyet kaydı için)
    const previousWorkshop = workshops.find(
      (w) => w.workshopId === oldWorkshopId,
    );

    const isMoveToCompleted =
      targetWorkshop &&
      (targetWorkshop.name.toLowerCase().includes("biten") ||
        targetWorkshop.name.toLowerCase().includes("done") ||
        targetWorkshop.name.toLowerCase().includes("tamamlanan") ||
        targetWorkshop.name.toLowerCase().includes("tamamlandı") ||
        targetWorkshop.name.toLowerCase().includes("tamamlan"));

    try {
      // Maliyetleri kaydet (varsa)
      if (costs.length > 0) {
        // ModelCost.ModelId zorunlu - eksikse sessizce atlamak yerine hata ver
        if (!draggedOrder.modelId) {
          throw new Error(
            "Siparişin model bilgisi alınamadığı için maliyet kaydedilemedi.",
          );
        }

        for (const cost of costs) {
          const modelCostData = {
            modelId: draggedOrder.modelId,
            orderId: orderId,
            costItemId: cost.costItemId,
            quantity: cost.quantityUsed,
            unit: cost.unit, // Cost'tan gelen unit bilgisi
            quantity2: cost.quantity2, // İkinci boyut (opsiyonel)
            quantity3: cost.quantity3, // Üçüncü boyut (opsiyonel, referans)
            unit2: cost.unit2, // İkinci birim (opsiyonel)
            unit3: cost.unit3, // Üçüncü birim (opsiyonel, referans)
            costUnitId3: cost.costUnitId3, // Üçüncü birim ID (referans)
            unitPrice: cost.actualPrice,
            totalCost: cost.totalCost, // CustomCost için direkt toplam tutar
            currency: cost.currency,
            usage: previousWorkshop?.name || undefined, // Önceki atölye
            notes: cost.notes || "", // Not alanı
            priority: 1,
            isActive: true,
            usdRate: usdRate || undefined,
            eurRate: eurRate || undefined,
            gbpRate: gbpRate || undefined,
            exchangeRateDate: new Date().toISOString(),
          };

          await costService.addModelCost(modelCostData);
        }
      }

      // Status güncelleme mantığı
      // 1. "Biten İşler"e taşındıysa -> Tamamlandı
      // 2. Atanmadı'dan (oldWorkshopId yok) bir atölyeye atandıysa -> İşlemde
      // 3. Bir atölyeden başka atölyeye taşındıysa -> İşlemde kalsın
      let newStatus = draggedOrder.status;

      if (isMoveToCompleted) {
        newStatus = OrderStatus.Tamamlandi;
      } else if (!oldWorkshopId && newWorkshopId) {
        // Atanmadı'dan bir atölyeye atandı
        newStatus = OrderStatus.Islemde;
      } else if (
        oldWorkshopId &&
        newWorkshopId &&
        draggedOrder.status === OrderStatus.Atanmadi
      ) {
        // Eğer bir şekilde status hala Atanmadı ise İşlemde yap
        newStatus = OrderStatus.Islemde;
      }

      // Sonra workshop'u güncelle (artık userId gönderme)
      // Yeni atama endpointi için payload
      const assignPayload = {
        workshopId: newWorkshopId,
        orderStatusId: newStatus,
      };
      await orderService.assign(orderId, assignPayload);
      // UI'ı güncelle
      await loadData();

      // Modal'ı kapat
      setShowTransferModal(false);
      setPendingWorkshopChange(null);
      setSelectedUserId("");

      let message = newWorkshopId
        ? `Sipariş "${targetWorkshop?.name}" atölyesine atandı`
        : "Sipariş atölyeden kaldırıldı";

      if (costs.length > 0) {
        message += `\n${costs.length} maliyet kalemi kaydedildi.`;
      }

      if (isMoveToCompleted) {
        message += " ve tamamlandı olarak işaretlendi! 🎉";
      }

      setAlertModal({ isOpen: true, message, type: "success" });
    } catch (error: any) {
      console.error("❌ Failed to update workshop:", error);

      // Hata durumunda geri al
      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.orderId === orderId
            ? {
                ...o,
                workshopId: oldWorkshopId,
                operatorId: draggedOrder.operatorId,
              }
            : o,
        ),
      );

      setAlertModal({
        isOpen: true,
        message: `Atölye güncellenemedi!\n${error.response?.data?.message || error.message || "Bilinmeyen hata"}`,
        type: "error",
      });
    }
  };

  const handleTransferCancel = () => {
    setShowTransferModal(false);
    setPendingWorkshopChange(null);
    setSelectedUserId("");
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  if (loading) {
    return <PageLoader message="Atölye verileri yükleniyor..." />;
  }

  const fmtTRY = (n: number) =>
    new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(n);
  const visibleOrders = orders.filter(matchesFilters);
  const totalActive = visibleOrders.length;
  const totalValueTRY = calculateColumnTotal(visibleOrders);
  const overdueCount = orders.filter((o) => {
    if (!o.deadline || new Date(o.deadline) >= new Date()) return false;
    const w = workshops.find((ws) => ws.workshopId === o.workshopId);
    return !(w && isCompletedWorkshop(w.name));
  }).length;

  // Kolonlar: her atölyeye kurumsal aksan rengi
  const columns = workshops.map((workshop, idx) => ({
    id: workshop.workshopId,
    title: workshop.name,
    color: getAccent(workshop.name, idx),
  }));

  return (
    <div className="kanban-container workshop-kanban-page">
      <div className="wk-header">
        <div className="wk-breadcrumb">
          <span>Atölye Yönetimi</span>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span className="active">Kanban</span>
        </div>
        <div className="wk-head-main">
          <div className="wk-head-left">
            <div className="wk-head-icon">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 20h20M4 20V8l6 4V8l6 4V8l4 2v10" />
                <path d="M9 20v-4h2v4" />
              </svg>
            </div>
            <div>
              <h1>Atölye Kanban</h1>
              <p>Siparişleri atölyeler arasında sürükleyerek yönetin</p>
            </div>
          </div>
          <div className="wk-toolbar">
            <div className="wk-stats">
              <div className="wk-stat">
                <span className="wk-stat-value is-brand">{totalActive}</span>
                <span className="wk-stat-label">Aktif sipariş</span>
              </div>
              <div className="wk-stat">
                <span className="wk-stat-value">{fmtTRY(totalValueTRY)}</span>
                <span className="wk-stat-label">Toplam değer</span>
              </div>
              <div className="wk-stat">
                <span className="wk-stat-value">{workshops.length}</span>
                <span className="wk-stat-label">Atölye</span>
              </div>
              <div className="wk-stat">
                <span className={`wk-stat-value ${overdueCount > 0 ? "is-danger" : ""}`}>
                  {overdueCount}
                </span>
                <span className="wk-stat-label">Geciken</span>
              </div>
            </div>
            <div className="wk-search">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Firma / model ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="wk-select"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">Tüm öncelikler</option>
              <option value="Yüksek">Yüksek</option>
              <option value="Orta">Orta</option>
              <option value="Düşük">Düşük</option>
            </select>
            <button className="btn btn-secondary" onClick={loadData} title="Yenile">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Yenile
            </button>
          </div>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="kanban-board" ref={kanbanBoardRef}>
          {columns.map((column) => {
            const columnOrders = getOrdersByWorkshop(column.id);
            const columnOrderIds = columnOrders.map((o) => o.orderId);
            const columnTotal = calculateColumnTotal(columnOrders);

            return (
              <KanbanColumn
                key={column.id}
                id={column.id}
                title={column.title}
                color={column.color}
                count={columnOrders.length}
                totalValue={columnTotal}
              >
                <SortableContext
                  items={columnOrderIds}
                  strategy={verticalListSortingStrategy}
                  id={column.id}
                >
                  <div className="kanban-cards">
                    {columnOrders.length === 0 && (
                      <div className="empty-column">Sipariş bulunmuyor</div>
                    )}
                    {columnOrders.map((order) => (
                      <KanbanCard
                        key={order.orderId}
                        order={order}
                        formatDate={formatDate}
                        workshopDuration={orderDurations[order.orderId]}
                        onEdit={handleEditOrder}
                        onDetail={handleDetailOrder}
                      />
                    ))}
                  </div>
                </SortableContext>
              </KanbanColumn>
            );
          })}
        </div>

        {/* Drag overlay - sürüklerken görünen kart */}
        <DragOverlay>
          {activeOrder ? (
            <div className="kanban-drag-overlay">
              <div className="kdo-model">
                {activeOrder.model?.modelCode || "-"}
              </div>
              <div className="kdo-firm">
                {activeOrder.firm?.firmName || "—"}
              </div>
              <div className="kdo-meta">
                <span>{activeOrder.quantity} ad</span>
                <span>
                  {(() => {
                    const basePrice =
                      (activeOrder.price || 0) * activeOrder.quantity;
                    const currency =
                      activeOrder.priceCurrency ||
                      activeOrder.currency ||
                      "TRY";
                    const currencySymbol =
                      currency === "USD"
                        ? "$"
                        : currency === "EUR"
                          ? "€"
                          : currency === "TRY"
                            ? "₺"
                            : currency;
                    return `${currencySymbol}${basePrice.toLocaleString("tr-TR", {
                      maximumFractionDigits: 0,
                    })}`;
                  })()}
                </span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Workshop Transfer Modal (Maliyet) */}
      {showTransferModal && pendingWorkshopChange && (
        <WorkshopTransferModal
          isOpen={showTransferModal}
          orderId={pendingWorkshopChange.orderId}
          orderQuantity={pendingWorkshopChange.orderQuantity}
          oldWorkshopId={pendingWorkshopChange.oldWorkshopId || null}
          oldWorkshopName={
            workshops.find(
              (w) => w.workshopId === pendingWorkshopChange.oldWorkshopId,
            )?.name || "Atanmamış"
          }
          newWorkshopId={pendingWorkshopChange.newWorkshopId || null}
          newWorkshopName={
            workshops.find(
              (w) => w.workshopId === pendingWorkshopChange.newWorkshopId,
            )?.name || "Yeni Atölye"
          }
          currentUser={user || undefined}
          onClose={handleTransferCancel}
          onSave={handleTransferSave}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Order Edit Modal */}
      {editingOrderId && (
        <OrderEditModal
          isOpen={editModalOpen}
          orderId={editingOrderId}
          onClose={() => {
            setEditModalOpen(false);
            setEditingOrderId(null);
          }}
          onSave={handleOrderUpdated}
        />
      )}

      {/* Order Detail Modal */}
      {detailOrderId && (
        <OrderDetailModal
          isOpen={detailModalOpen}
          orderId={detailOrderId}
          onClose={() => {
            setDetailModalOpen(false);
            setDetailOrderId(null);
          }}
        />
      )}
    </div>
  );
};

export default WorkshopKanban;
